import { Injectable } from '@angular/core';

export interface MobileSessionState {
  tireSpecSessionGuid: string;
  deviceInstanceId: string;
  currentRoute: string;
  capturedImage: string | null;
  ocrResult: any | null;
  formState: any | null;
  isManualEntry: boolean;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class MobileStorageService {
  private readonly dbName = 'TireSpecMobileDB';
  private readonly storeName = 'session_states';
  private readonly dbVersion = 1;
  private dbPromise: Promise<IDBDatabase> | null = null;

  private getDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        reject(new Error('IndexedDB is not supported in this environment.'));
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName, { keyPath: 'tireSpecSessionGuid' });
        }
      };

      request.onsuccess = (event: Event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        resolve(db);
      };

      request.onerror = (event: Event) => {
        console.error('Error opening IndexedDB:', (event.target as IDBOpenDBRequest).error);
        reject((event.target as IDBOpenDBRequest).error);
      };
    });

    return this.dbPromise;
  }

  async saveState(guid: string, state: Partial<MobileSessionState>): Promise<void> {
    if (!guid) {
      return;
    }
    try {
      const existing = (await this.getState(guid)) || {};
      const fullState: MobileSessionState = {
        tireSpecSessionGuid: guid,
        deviceInstanceId: state.deviceInstanceId ?? (existing as any).deviceInstanceId ?? '',
        currentRoute: state.currentRoute ?? (existing as any).currentRoute ?? '/home',
        capturedImage: state.capturedImage !== undefined ? state.capturedImage : ((existing as any).capturedImage ?? null),
        ocrResult: state.ocrResult !== undefined ? state.ocrResult : ((existing as any).ocrResult ?? null),
        formState: state.formState !== undefined ? state.formState : ((existing as any).formState ?? null),
        isManualEntry: state.isManualEntry !== undefined ? state.isManualEntry : ((existing as any).isManualEntry ?? false),
        timestamp: Date.now()
      };

      const db = await this.getDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const req = store.put(fullState);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.error('Failed to save state to IndexedDB:', err);
    }
  }

  async getState(guid: string): Promise<MobileSessionState | null> {
    if (!guid) {
      return null;
    }
    try {
      const db = await this.getDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readonly');
        const store = transaction.objectStore(this.storeName);
        const req = store.get(guid);
        req.onsuccess = () => resolve((req.result as MobileSessionState) || null);
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.error('Failed to get state from IndexedDB:', err);
      return null;
    }
  }

  async clearState(guid: string): Promise<void> {
    if (!guid) {
      return;
    }
    try {
      const db = await this.getDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const req = store.delete(guid);
        req.onsuccess = () => {
          console.log(`IndexedDB state cleared for session ${guid}`);
          resolve();
        };
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.error('Failed to clear state from IndexedDB:', err);
    }
  }

  async purgeOldSessions(maxAgeMs: number = 24 * 60 * 60 * 1000): Promise<void> {
    try {
      const db = await this.getDB();
      const now = Date.now();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(this.storeName, 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const req = store.openCursor();
        req.onsuccess = (event: Event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            const record = cursor.value as MobileSessionState;
            if (now - (record.timestamp || 0) > maxAgeMs) {
              cursor.delete();
            }
            cursor.continue();
          } else {
            resolve();
          }
        };
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.error('Failed to purge old sessions from IndexedDB:', err);
    }
  }
}
